import { differenceInMilliseconds, minutesToMilliseconds, parseISO } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';
import { useInterval } from 'usehooks-ts';

import { useGetTelehealthSessionInfo } from '../../hooks';

export type IntentionUse = 'set' | 'shared' | 'not-set-active' | 'not-set';

const MINUTES_THRESHOLD = 10;
const POLLING_INTERVAL_MS = 1000;

/**
 * Checks if current time is within threshold minutes of session end
 * Returns false if appointmentEndTime is not available
 */
function isWithinSessionEndThreshold(appointmentEndTime?: string): boolean {
  if (!appointmentEndTime) {
    return false;
  }

  try {
    const endDate = parseISO(appointmentEndTime);
    const now = new Date();
    const msRemaining = differenceInMilliseconds(endDate, now);
    return msRemaining <= minutesToMilliseconds(MINUTES_THRESHOLD);
  } catch {
    return false;
  }
}

function determineIntentionUse(
  intentionCompletedAt: string | undefined,
  intentionPromptSentAt: string | undefined,
  isNearSessionEnd: boolean,
): IntentionUse {
  if (intentionCompletedAt) return 'set';
  if (intentionPromptSentAt) return 'shared';
  if (isNearSessionEnd) return 'not-set-active';
  return 'not-set';
}

/**
 * Hook to determine the intention use state with smart polling.
 * Polls every second to check if the session crosses the 10-minute threshold,
 * but stops polling once unnecessary (state is no longer 'set').
 *
 * @param providerShortId - Provider's short ID to fetch session info
 * @param patientShortId - Patient's short ID to fetch session info
 * @param intentionCompletedAt - Timestamp when intention was completed by patient
 * @param intentionPromptSentAt - Timestamp when prompt was sent to patient
 * @returns The intention use state: 'set' | 'shared' | 'not-set-active' | 'not-set'
 */
export function useIntentionUse(
  providerShortId: string | undefined,
  patientShortId: string | undefined,
  intentionCompletedAt: string | undefined,
  intentionPromptSentAt: string | undefined,
): IntentionUse {
  const [isNearEnd, setIsNearEnd] = useState<boolean>(false);
  const { data, loading } = useGetTelehealthSessionInfo(providerShortId, patientShortId);
  const appointmentEndTime = data?.telehealthSessionInfo?.appointment?.timeEnd;

  // Poll to check if we've crossed the 10-minute threshold
  useInterval(
    useCallback(() => {
      if (appointmentEndTime && !intentionCompletedAt && !intentionPromptSentAt) {
        setIsNearEnd(isWithinSessionEndThreshold(appointmentEndTime));
      }
    }, [appointmentEndTime, intentionCompletedAt, intentionPromptSentAt]),
    // do not continue poll if the intention has been shared or completed, we've already reached the 10 min
    // threshold or we already fetched data and there isn't an appointment time end
    intentionCompletedAt || intentionPromptSentAt || isNearEnd || (!loading && !appointmentEndTime)
      ? null
      : POLLING_INTERVAL_MS,
  );

  return useMemo(() => {
    return determineIntentionUse(intentionCompletedAt, intentionPromptSentAt, isNearEnd);
  }, [intentionCompletedAt, intentionPromptSentAt, isNearEnd]);
}
